import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import { Chip } from "components/DesignSystem/Chips/Chips";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { Search } from "components/DesignSystem/Search/Search";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { DateFilter } from "components/Filters/DateFilter";
import { DownloadIcon } from "components/icons/Download";
import {
  NO_SERVICE_TEAM_NAME,
  NO_SERVICE_TEAM_UUID,
} from "constants/settingServiceProvider";
import { COMPANY_GROUP_TAG } from "constants/tagCategory";
import { NO_TAG, NO_TAG_KEY } from "constants/tagColors";
import {
  BOOKS_COMPLETED,
  BOOKS_IN_PROGRESS,
  BOOKS_NOT_STARTED,
  ETD_FILTER_OPTIONS,
  MISSING_BUSINESS_FINANCIALS_FORM_7004_FLAG,
  MISSING_INFORMATION_FORM_7004_FLAG,
  NON_PROFITABLE,
  PROFITABLE,
} from "constants/task";
import { CRM_SUBSCRIPTION_TYPES } from "dictionaries";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { ChangeEvent, MouseEvent, useContext, useMemo } from "react";
import { useGetTagsForCategoryQuery } from "store/apis/billing";
import { useGetAllInkleCPATeamsQuery } from "store/apis/internalTeamSetting";
import {
  useGetServiceTeamQuery,
  useLazyGetAllServiceProviderTaskQuery,
} from "store/apis/serviceTeam";
import { Tag } from "types/Models/FCA";
import { formatDate } from "utils/formatDate";
import { TASK_SORT_BY, sortOptions } from "../../../constants/foreignCATask";
import authContext from "../../../jwt_context&axios/authContext";
import SortDescendingIcon from "../../../static/images/SortDescending.svg";

import Loader from "components/DesignSystem/Loader/loader";
import { US_UUID } from "constants/regAgent";
import { NOT_STARTED, NOT_STARTED_KEY } from "constants/taskStatus";
import { useFilters } from "hooks/useFilter";
import { useToast } from "hooks/useToast";
import { useGetTaskTemplatesQuery } from "store/apis/getTemplates";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import {
  useGetAllTaskStatesQuery,
  useGetTaskTagQuery,
} from "store/apis/taskTags";
import { useGetCountryStatesQuery } from "store/apis/teamSetting";
import { BackendError } from "types/utils/error";
import { FilterType } from "./ForeignCATask";
import { validate } from "uuid";
import SortFunnel from "static/images/SortFunnel.svg";
import { useLazyExportCSVQuery } from "store/apis/task";

type ForeignCATaskFilterType = {
  children?: React.ReactNode;
  searchedTerm: string | null;
  selectedTag: string | null;
  selectedCompanyTag: string | null;
  tags: Tag[];
  selectedStateFilter: string | null;
  taskStates: { name: string; uuid: string }[];
  selectedTemplateFilter: string | null;
  selectedAssignee: string | null;
  assignees: {
    name: string;
    profile_id: string;
  }[];
  selectedSortOption: {
    id: number;
    title: string;
    value: string;
    sortKey: string | null;
  };
  setSelectedSortOption: (
    newSate: React.SetStateAction<{
      id: number;
      title: string;
      value: string;
      sortKey: string | null;
    }>
  ) => void;
  showSortFeature: boolean;
  update: ({
    query,
    value,
  }: {
    query: string | null;
    value: boolean | number | string | null;
  }) => void;
  totalCount: number;
  selectedSubscription: string | null;
  paginationData: {
    totalPage: number;
    currentPage: number;
    itemsPerPage: number;
    totalItemCount: number;
  };
  isLoading: boolean;
  selectedETD: string | null;
  selectedTeams: string | null;
  selectedMembers: string | null;
  values: FilterType;
  updateFilter: <S extends keyof FilterType>(
    name: S,
    newValue: FilterType[S]
  ) => void;
  serviceTeamId?: string;
  showUnassignedTeam: string | null;
  showUnassignedPreparer: string | null;
  taskFilters: string;
  selectedFilingInfo: string | null;
  selectedStateRegistrations: string | null;
  selectedFormationType: string | null;
};

export const filingInfoOptions = [
  {
    value: MISSING_INFORMATION_FORM_7004_FLAG,
    label: "Missing Info",
  },
  {
    value: MISSING_BUSINESS_FINANCIALS_FORM_7004_FLAG,
    label: "Missing Financials",
  },
  {
    value: PROFITABLE,
    label: "Profitable",
  },
  {
    value: NON_PROFITABLE,
    label: "Non Profitable",
  },
  {
    value: BOOKS_COMPLETED,
    label: "Books Completed",
  },
  {
    value: BOOKS_IN_PROGRESS,
    label: "Books In Progress",
  },
  {
    value: BOOKS_NOT_STARTED,
    label: "Books Not Started",
  },
];

export const TagFilter = ({
  onUpdate,
  selectedTag,
}: {
  onUpdate: (values: string[]) => void;
  selectedTag?: string | null;
}) => {
  const { data: tagsData = [] } = useGetTaskTagQuery();

  const tags = useMemo(
    () => [
      ...tagsData,
      {
        title: NO_TAG,
        uuid: NO_TAG_KEY,
        bg_color_hex: "",
        color_hex: "",
        tag_category: 0,
      },
    ],
    [tagsData]
  );

  if (!tags) return null;

  return (
    <MultiSelectFilter
      onChange={onUpdate}
      options={tags.map((tag) => ({
        value: tag.uuid,
        label: tag.title,
      }))}
      selected={selectedTag?.split(",")}
    />
  );
};

export const TaskServiceTeamFilter = ({
  onUpdate,
  selectedTeams,
}: {
  onUpdate: (values: string[]) => void;
  selectedTeams?: string | null;
}) => {
  const { isAdmin } = useRoleBasedView();
  const { data: teamOptions = [] } = useGetAllInkleCPATeamsQuery(
    {
      accessibleTeams: "CPA_TEAM",
    },
    { skip: !isAdmin }
  );

  const serviceTeamOptions = useMemo(
    () => [
      ...teamOptions,
      { uuid: NO_SERVICE_TEAM_UUID, name: NO_SERVICE_TEAM_NAME },
    ],
    [teamOptions]
  );

  if (!serviceTeamOptions.length) return null;

  return (
    <MultiSelectFilter
      onChange={onUpdate}
      options={serviceTeamOptions?.map((team) => ({
        value: team.uuid,
        label: team.name,
      }))}
      selected={selectedTeams?.split(",")}
    />
  );
};

export const StateFilter = ({
  onUpdate,
  selectedStateFilter,
}: {
  onUpdate: (values: string[]) => void;
  selectedStateFilter?: string | null;
}) => {
  const { data: taskStatesData = [] } = useGetAllTaskStatesQuery();

  const taskStates = [
    ...taskStatesData,
    { name: NOT_STARTED, uuid: NOT_STARTED_KEY },
  ];

  if (!taskStates || !taskStates.length) return null;

  return (
    <MultiSelectFilter
      onChange={onUpdate}
      options={taskStates.map((taskState) => ({
        value: taskState.uuid,
        label: taskState.name,
      }))}
      selected={selectedStateFilter?.split(",")}
    />
  );
};

export const FilingsFilter = ({
  onUpdate,
  selectedTemplateFilter,
}: {
  onUpdate: (values: string[]) => void;
  selectedTemplateFilter?: string | null;
}) => {
  const { data, isLoading } = useGetTaskTemplatesQuery();
  const { templates = [] } = data || {};

  if (isLoading) {
    return (
      <div className="t-w-full t-h-full t-flex t-items-center t-justify-center">
        <Loader size="small" />
      </div>
    );
  }

  if (!templates || !templates.length) return null;

  return (
    <MultiSelectFilter
      onChange={onUpdate}
      options={templates.map((template) => ({
        value: template.uuid,
        label: `${template.title}  ${
          !template.year ? "" : `- ${template.year}`
        }`,
      }))}
      selected={selectedTemplateFilter?.split(",")}
    />
  );
};

export const ForeignCATaskFilter = ({
  searchedTerm,
  selectedTag,
  selectedCompanyTag,
  tags,
  selectedStateFilter,
  taskStates,
  selectedTemplateFilter,
  selectedAssignee,
  assignees,
  selectedSortOption,
  setSelectedSortOption,
  showSortFeature,
  update,
  selectedSubscription,
  paginationData,
  isLoading,
  selectedETD,
  selectedTeams,
  selectedMembers,
  values,
  updateFilter,
  serviceTeamId,
  showUnassignedTeam,
  showUnassignedPreparer,
  taskFilters,
  selectedFilingInfo,
  selectedStateRegistrations,
  selectedFormationType,
  children,
}: ForeignCATaskFilterType) => {
  const { alertToast, successToast } = useToast();
  const { authtoken } = useContext(authContext);
  const {
    isCpa,
    isAdmin,
    isServiceSuperAdmin,
    isForeignCA,
    isUserTypeForeignCA,
  } = useRoleBasedView();
  const [exportCSV, { isLoading: exportingCsv }] = useLazyExportCSVQuery();
  const isFCA = isUserTypeForeignCA || isForeignCA;

  const { updateFilter: updateSortFilter } = useFilters({
    initialValue: { task_sort_by: selectedSortOption.sortKey },
    useQueryParams: true,
  });
  const { data: companyTagsData = [] } = useGetTagsForCategoryQuery({
    tagType: COMPANY_GROUP_TAG,
  });

  const serviceTeamIds = (selectedTeams || serviceTeamId)
    ?.split(",")
    .filter((id) => validate(id))
    .join(",");

  const { data: teamMembers = [] } = useGetServiceTeamQuery(
    {
      serviceTeamId: serviceTeamIds,
    },
    { skip: !serviceTeamIds, refetchOnMountOrArgChange: true }
  );
  const companyTags = [...companyTagsData, { title: NO_TAG, uuid: NO_TAG_KEY }];
  const selectedEtdLabel = ETD_FILTER_OPTIONS.find(
    ({ value }) => value === selectedETD
  )?.label;

  const { data: states, isLoading: isStatesLoading } = useGetCountryStatesQuery(
    {
      countryId: US_UUID,
    }
  );
  const { isLoading: isCountiesLoading, data } = useGetCountriesDataQuery();
  const US_FORMATION_TYPE =
    data?.find(
      ({ name, code_alpha_2 }) =>
        code_alpha_2 === "US" && name === "United States"
    )?.formation_type || [];

  const [getAllServiceProviderTask, { isFetching }] =
    useLazyGetAllServiceProviderTaskQuery();

  const stateRegistrationOptions =
    states?.map(({ name, state_id }) => ({
      label: name,
      value: state_id,
    })) || [];

  const searchQuery = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value) {
      update({ query: "search_term", value: value });
    } else {
      update({ query: "search_term", value: null });
    }
  };

  const templateFilter = (values: string[] | null) => {
    if (values) {
      update({ query: "task_template_filter_uuids", value: values.join(",") });
    } else {
      update({
        query: "task_template_filter_uuids",
        value: null,
      });
    }
  };

  const subscriptionFilter = (values: string[] | null) => {
    if (values) {
      update({ query: "subscription_filter", value: values.join(",") });
    } else {
      update({ query: "subscription_filter", value: null });
    }
  };

  const serviceTeamFilter = (values: string[] | null) => {
    if (values)
      update({ query: "service_team_uuids", value: values.join(",") || null });
  };

  const teamMemberFilter = (values: string[] | null) => {
    if (values)
      update({ query: "team_member_uuids", value: values.join(",") || null });
  };

  const handleEtdFilter = (value: string) => {
    update({
      query: "etd_filter",
      value: value || null,
    });
  };

  const updateTagFilter = (values: string[] | null) => {
    if (values) {
      update({ query: "task_tag_filter_uuids", value: values.join(",") });
    } else {
      update({ query: "task_tag_filter_uuids", value: null });
    }
  };

  const updateCompanyTagFilter = (values: string[] | null) => {
    if (values) {
      update({ query: "company_tag_filter_uuids", value: values.join(",") });
    } else {
      update({ query: "company_tag_filter_uuids", value: null });
    }
  };

  const stateFilter = (values: string[] | null) => {
    if (values) {
      update({ query: "task_state_filter_uuids", value: values.join(",") });
    } else {
      update({ query: "task_state_filter_uuids", value: null });
    }
  };

  const formationTypeFilter = (values: string[] | null) => {
    if (values) {
      update({ query: "formation_type_filter_uuids", value: values.join(",") });
    } else {
      update({ query: "formation_type_filter_uuids", value: null });
    }
  };

  const updateAssigneeFilter = (values: string[] | null) => {
    if (values) {
      update({ query: "assignee_uuids", value: values.join(",") });
    } else {
      update({ query: "assignee_uuids", value: null });
    }
  };

  const updateStateRegistrationsFilter = (values: string[] | null) => {
    if (values) {
      update({ query: "state_registrations", value: values.join(",") });
    } else {
      update({ query: "state_registrations", value: null });
    }
  };

  const updateFilingInfoFilter = (values: string[] | null) => {
    if (values) {
      update({ query: "filing_infos", value: values.join(",") });
    } else {
      update({ query: "filing_infos", value: null });
    }
  };

  const updateNoTeamFilter = () => {
    update({
      query: "show_unassigned_team",
      value: showUnassignedTeam ? null : true,
    });
  };

  const updateNoAssigneeFilter = () => {
    update({
      query: "show_unassigned_preparer",
      value: showUnassignedPreparer ? null : true,
    });
  };

  const downloadCSV = async (exportToMail = true) => {
    try {
      const res = await getAllServiceProviderTask({
        ...JSON.parse(taskFilters),
        generate_report: true,
        export_to_mail: exportToMail,
      }).unwrap();

      if (res?.download_url) {
        window.open(res?.download_url);
      }

      successToast({
        message: exportToMail
          ? "Filing report has been sent to your email"
          : "Filing report downloaded successfully",
      });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const generate7004CSV = async () => {
    try {
      const csvData = await exportCSV({
        ...JSON.parse(taskFilters),
        task_type_key: "FORM_7004",
      }).unwrap();

      const linkElement = document.createElement("a");
      linkElement.target = "_blank";
      linkElement.href = `data:${csvData.ContentType};base64,${csvData.Content}`;
      linkElement.download = `Inkle US Tax Deadlines Calendar ${formatDate()}`;
      linkElement.click();
      linkElement.remove();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  function setSortOption(
    e: MouseEvent<HTMLParagraphElement>,
    sortOption: {
      id: number;
      title: string;
      value: string;
      sortKey: string | null;
    }
  ) {
    setSelectedSortOption(sortOption);
    e.stopPropagation();
  }

  const selectedServiceTeamIds = ((isAdmin && selectedTeams) || serviceTeamId)
    ?.split(",")
    .filter((id: string) => validate(id))
    .join(",");

  const sortOptionsByKey = Object.fromEntries(
    sortOptions.map((option) => [option.sortKey, option])
  );

  const sortGroups = [
    {
      label: "Sort by status update",
      options: [
        sortOptionsByKey[TASK_SORT_BY.LATEST_MOVED_FIRST],
        sortOptionsByKey[TASK_SORT_BY.OLDEST_MOVED_FIRST],
      ],
    },
    {
      label: "Sort by launch date",
      options: [
        sortOptionsByKey[TASK_SORT_BY.OLDEST_LAUNCH_DATE],
        sortOptionsByKey[TASK_SORT_BY.LATEST_LAUNCH_DATE],
      ],
    },
    {
      label: "Sort by deadlines",
      options: [
        sortOptionsByKey[TASK_SORT_BY.PAST_DEADLINES],
        sortOptionsByKey[TASK_SORT_BY.UPCOMING_DEADLINES],
      ],
    },
  ];

  const handleSortChange = (value: string) => {
    const option = sortOptions.find((o) => o.value === value);
    if (!option) return;

    setSelectedSortOption(option);
    updateSortFilter("task_sort_by", option.sortKey);
  };

  return (
    <div className="t-w-full t-space-y-4">
      <div className="t-flex t-justify-between t-items-center">
        <div className="t-flex t-gap-x-2 t-items-center">
          {children}
          <div className="t-w-1/2 t-flex t-items-center">
            <Search
              placeholder="Search..."
              defaultValue={searchedTerm || undefined}
              onChange={searchQuery}
              block
              autoFocus
              className="t-h-8"
            />
          </div>
        </div>

        {!isFCA && (
          <div className="t-relative t-flex t-gap-4">
            <Dropdown.Root>
              <Dropdown.Trigger asChild className="t-group">
                <div>
                  <Button
                    size="small"
                    customType="icon"
                    onClick={(e) => e.stopPropagation()}
                    isLoading={isFetching}
                    disabled={isFetching}
                  >
                    <div className="t-flex t-items-center t-gap-1.5">
                      <DownloadIcon color="#706A85" size={20} />
                    </div>
                  </Button>
                </div>
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content sideOffset={4} align="end" side="bottom">
                  <Dropdown.Item onSelect={generate7004CSV}>
                    Generate 7004 CSV
                  </Dropdown.Item>
                  <Dropdown.Sub>
                    <Dropdown.SubTrigger className="t-group t-outline-none hover:t-outline-none focus-visible:t-outline-none">
                      Export
                    </Dropdown.SubTrigger>
                    <Dropdown.Portal>
                      <Dropdown.SubContent sideOffset={10} alignOffset={-5}>
                        <ExportOptions
                          onLocalDownload={() => downloadCSV(false)}
                          onMailExport={() => downloadCSV(true)}
                        />
                      </Dropdown.SubContent>
                    </Dropdown.Portal>
                  </Dropdown.Sub>
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
          </div>
        )}
      </div>

      <div className="t-flex t-items-center t-justify-between">
        <div className="t-flex t-items-center t-gap-1.5">
          {showSortFeature && (
            <Dropdown.Root>
              <Dropdown.Trigger asChild>
                <Button size="xsmall" onClick={(e) => e.stopPropagation()}>
                  <div className="t-flex t-items-center t-gap-1.5">
                    <img
                      src={SortFunnel}
                      alt="icon"
                      height="16px"
                      width="16px"
                    />
                    <span className="t-text-sm t-text-text-70">Sort By:</span>
                    <span className="t-text-sm t-text-text-90 t-font-medium">
                      {selectedSortOption?.title}
                    </span>
                  </div>
                </Button>
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content
                  sideOffset={4}
                  align="start"
                  side="bottom"
                  className="t-px-2 t-py-3"
                >
                  <Radio.Root
                    value={selectedSortOption?.value}
                    onValueChange={handleSortChange}
                    className="t-space-y-[5px]"
                  >
                    {sortGroups.map((group) => (
                      <div key={group.label}>
                        <span
                          className="t-text-text-30 t-text-body-sm t-w-full
                        t-flex t-items-center t-self-stretch t-px-3"
                        >
                          {group.label}
                        </span>
                        <Radio.Content className="t-flex t-flex-col t-gap-y-1.5 t-py-1.5 t-px-3">
                          {group.options.map((option) => (
                            <Radio.Item
                              key={option.value}
                              value={option.value}
                              className="t-text-text-100 t-text-body-sm t-flex t-items-center t-gap-x-2"
                            >
                              {option.title}
                            </Radio.Item>
                          ))}
                        </Radio.Content>
                      </div>
                    ))}
                  </Radio.Root>
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
          )}
          <div className="t-flex t-items-center t-gap-2">
            <Filter.Root
              defaultValue="STATE"
              title="Filter"
              capsule={
                <>
                  {values.FORMATION_TYPE.length > 0 && (
                    <Chip
                      onRemove={() => updateFilter("FORMATION_TYPE", [])}
                      filterType="FORMATION_TYPE"
                      isActive
                    >
                      Formation Type ({values.FORMATION_TYPE.length})
                    </Chip>
                  )}
                  {isAdmin && (
                    <Chip
                      onRemove={updateNoTeamFilter}
                      filterType="SERVICE_TEAM"
                      isActive={Boolean(showUnassignedTeam)}
                      isFixedFilter
                      onClick={updateNoTeamFilter}
                    >
                      Unassigned Team
                    </Chip>
                  )}
                  {(isServiceSuperAdmin || isAdmin) && (
                    <Chip
                      onRemove={updateNoAssigneeFilter}
                      filterType="SERVICE_TEAM_MEMBER"
                      isActive={Boolean(showUnassignedPreparer)}
                      isFixedFilter
                      onClick={updateNoAssigneeFilter}
                    >
                      Unassigned Preparer
                    </Chip>
                  )}
                  {selectedStateFilter &&
                    selectedStateFilter?.split(",").length > 0 && (
                      <Chip
                        onRemove={() => stateFilter(null)}
                        isActive
                        filterType="STATE"
                      >
                        State ({selectedStateFilter.split(",").length})
                      </Chip>
                    )}
                  {selectedTemplateFilter &&
                    selectedTemplateFilter?.split(",").length > 0 && (
                      <Chip
                        onRemove={() => templateFilter(null)}
                        isActive
                        filterType="FILING"
                      >
                        Filing ({selectedTemplateFilter.split(",").length})
                      </Chip>
                    )}
                  {selectedAssignee &&
                    selectedAssignee?.split(",").length > 0 && (
                      <Chip
                        onRemove={() => updateAssigneeFilter(null)}
                        isActive
                        filterType="AGENT"
                      >
                        Agent ({selectedAssignee.split(",").length})
                      </Chip>
                    )}

                  {selectedSubscription &&
                    selectedSubscription?.split(",").length > 0 && (
                      <Chip
                        onRemove={() => subscriptionFilter(null)}
                        isActive
                        filterType="SUBSCRIPTION"
                      >
                        Subscription ({selectedSubscription.split(",").length})
                      </Chip>
                    )}
                  {selectedTag && selectedTag?.split(",").length > 0 && (
                    <Chip
                      onRemove={() => updateTagFilter(null)}
                      isActive
                      filterType="TAG"
                    >
                      Tag ({selectedTag.split(",").length})
                    </Chip>
                  )}
                  {selectedCompanyTag &&
                    selectedCompanyTag?.split(",").length > 0 && (
                      <Chip
                        onRemove={() => updateCompanyTagFilter(null)}
                        isActive
                        filterType="COMPANY_TAG"
                      >
                        Company Tag ({selectedCompanyTag.split(",").length})
                      </Chip>
                    )}
                  {selectedTeams && selectedTeams?.split(",").length > 0 && (
                    <Chip
                      onRemove={() => serviceTeamFilter([])}
                      isActive
                      filterType="SERVICE_TEAM"
                    >
                      Team ({selectedTeams?.split(",").length})
                    </Chip>
                  )}
                  {selectedMembers &&
                    selectedMembers?.split(",").length > 0 && (
                      <Chip
                        onRemove={() => teamMemberFilter([])}
                        isActive
                        filterType="SERVICE_TEAM_MEMBER"
                      >
                        Members ({selectedMembers?.split(",").length})
                      </Chip>
                    )}
                  {selectedETD && selectedEtdLabel && (
                    <Chip
                      onRemove={() => handleEtdFilter("")}
                      isActive
                      filterType="ETD"
                    >
                      {selectedEtdLabel}
                    </Chip>
                  )}
                  {values.START_DATE && (
                    <Chip
                      onRemove={() => {
                        updateFilter("START_DATE", "");
                        updateFilter("END_DATE", "");
                      }}
                      isActive
                      filterType="COMPLETED_DATE"
                    >
                      From: {formatDate(values.START_DATE)} To:{" "}
                      {formatDate(values.END_DATE)}
                    </Chip>
                  )}
                  {selectedStateRegistrations &&
                    selectedStateRegistrations?.split(",").length > 0 && (
                      <Chip
                        onRemove={() => updateStateRegistrationsFilter(null)}
                        isActive
                        filterType="STATE_REGISTRATIONS"
                      >
                        State Registrations (
                        {selectedStateRegistrations.split(",").length})
                      </Chip>
                    )}
                  {selectedFilingInfo &&
                    selectedFilingInfo?.split(",").length > 0 && (
                      <Chip
                        onRemove={() => updateFilingInfoFilter(null)}
                        isActive
                        filterType="FILING_INFO"
                      >
                        Filing Info ({selectedFilingInfo.split(",").length})
                      </Chip>
                    )}
                  {values.DEADLINE_START && (
                    <Chip
                      onRemove={() => {
                        updateFilter("DEADLINE_START", "");
                        updateFilter("DEADLINE_END", "");
                      }}
                      isActive
                      filterType="DEADLINE_DATE"
                    >
                      From: {formatDate(values.DEADLINE_START)} To:{" "}
                      {formatDate(values.DEADLINE_END)}
                    </Chip>
                  )}
                </>
              }
            >
              <Filter.Portal>
                <Filter.List>
                  {isAdmin && (
                    <Filter.ListItem value="STATE">State</Filter.ListItem>
                  )}
                  <Filter.ListItem value="FILING">Filing</Filter.ListItem>
                  {isAdmin && (
                    <>
                      <Filter.ListItem value="AGENT">Agent</Filter.ListItem>
                      <Filter.ListItem value="SUBSCRIPTION">
                        Subscription
                      </Filter.ListItem>
                      <Filter.ListItem value="TAG">Tag</Filter.ListItem>
                      <Filter.ListItem value="COMPANY_TAG">
                        Company Tag
                      </Filter.ListItem>
                      <Filter.ListItem value="SERVICE_TEAM">
                        Service Team
                      </Filter.ListItem>
                      <Filter.ListItem value="ETD">ETD</Filter.ListItem>
                      <Filter.ListItem value="COMPLETED_DATE">
                        Completed Date Filter
                      </Filter.ListItem>
                      <Filter.ListItem value="FORMATION_TYPE">
                        Formation Type
                      </Filter.ListItem>
                      <Filter.ListItem value="STATE_REGISTRATIONS">
                        State Registrations
                      </Filter.ListItem>
                      <Filter.ListItem value="FILING_INFO">
                        Filing Info
                      </Filter.ListItem>
                    </>
                  )}
                  {!isAdmin && (
                    <>
                      <Filter.ListItem value="DATE">Deadline</Filter.ListItem>
                    </>
                  )}
                </Filter.List>
                <Filter.Body value="DATE" block>
                  <DateFilter
                    values={{
                      START_DATE: values.DEADLINE_START,
                      END_DATE: values.DEADLINE_END,
                      SELECT_PERIOD: values.DEADLINE_SELECT_PERIOD,
                    }}
                    updateFilter={(name, value) => {
                      if (name === "START_DATE")
                        updateFilter("DEADLINE_START", value);
                      else if (name === "END_DATE")
                        updateFilter("DEADLINE_END", value);
                      else updateFilter("DEADLINE_SELECT_PERIOD", value);
                    }}
                    maxEndDate={null}
                  />
                </Filter.Body>
                <Filter.Body value="FORMATION_TYPE" block>
                  {isCountiesLoading ? (
                    <Loader size="small" />
                  ) : (
                    <MultiSelectFilter
                      isSearchable={false}
                      onChange={formationTypeFilter}
                      options={US_FORMATION_TYPE.map(({ name, uuid }) => ({
                        value: uuid,
                        label: name,
                      }))}
                      selected={selectedFormationType?.split(",")}
                    />
                  )}
                </Filter.Body>
                <Filter.Body value="STATE" block>
                  <StateFilter
                    onUpdate={stateFilter}
                    selectedStateFilter={selectedStateFilter}
                  />
                </Filter.Body>
                {!authtoken.is_foreign_ca && (
                  <Filter.Body value="AGENT" block>
                    <MultiSelectFilter
                      onChange={updateAssigneeFilter}
                      options={assignees?.map((assignee) => ({
                        value: assignee.profile_id,
                        label: assignee.name,
                      }))}
                      selected={selectedAssignee?.split(",")}
                    />
                  </Filter.Body>
                )}
                {isCpa && (
                  <Filter.Body value="SUBSCRIPTION" block>
                    <MultiSelectFilter
                      onChange={subscriptionFilter}
                      options={CRM_SUBSCRIPTION_TYPES?.map(
                        (subscription_name) => ({
                          value: subscription_name,
                          label: subscription_name,
                        })
                      )}
                      selected={selectedSubscription?.split(",")}
                    />
                  </Filter.Body>
                )}
                <Filter.Body value="FILING">
                  <FilingsFilter
                    onUpdate={templateFilter}
                    selectedTemplateFilter={selectedTemplateFilter}
                  />
                </Filter.Body>
                <Filter.Body value="TAG" block>
                  <TagFilter
                    onUpdate={updateTagFilter}
                    selectedTag={selectedTag}
                  />
                </Filter.Body>
                <Filter.Body value="COMPANY_TAG" block>
                  {companyTags && (
                    <MultiSelectFilter
                      onChange={updateCompanyTagFilter}
                      options={companyTags.map((tag) => ({
                        value: tag.uuid,
                        label: tag.title,
                      }))}
                      selected={selectedCompanyTag?.split(",")}
                    />
                  )}
                </Filter.Body>
                <Filter.Body value="SERVICE_TEAM" block>
                  <TaskServiceTeamFilter
                    onUpdate={serviceTeamFilter}
                    selectedTeams={selectedTeams}
                  />
                </Filter.Body>

                <Filter.Body value="SERVICE_TEAM_MEMBER" block>
                  <MultiSelectFilter
                    onChange={teamMemberFilter}
                    options={teamMembers?.map((member) => ({
                      value: member.uuid,
                      label: member.name,
                    }))}
                    selected={selectedMembers?.split(",")}
                  />
                </Filter.Body>
                <Filter.Body value="ETD">
                  <Radio.Root
                    value={selectedETD || undefined}
                    onValueChange={handleEtdFilter}
                  >
                    <Radio.Content className="t-gap-3 t-flex t-flex-col">
                      {ETD_FILTER_OPTIONS.map((item) => (
                        <Radio.Item key={item.value} value={item.value}>
                          {item.label}
                        </Radio.Item>
                      ))}
                    </Radio.Content>
                  </Radio.Root>
                </Filter.Body>
                <Filter.Body value="COMPLETED_DATE" block>
                  <DateFilter values={values} updateFilter={updateFilter} />
                </Filter.Body>
                <Filter.Body value="STATE_REGISTRATIONS" block>
                  {isStatesLoading ? (
                    <Loader size="small" />
                  ) : (
                    <MultiSelectFilter
                      onChange={updateStateRegistrationsFilter}
                      options={[
                        {
                          label: "No State Registrations",
                          value: "no_state_registrations",
                        },
                        ...stateRegistrationOptions,
                      ]}
                      selected={selectedStateRegistrations?.split(",")}
                    />
                  )}
                </Filter.Body>
                <Filter.Body value="FILING_INFO" block>
                  <MultiSelectFilter
                    onChange={updateFilingInfoFilter}
                    options={filingInfoOptions}
                    selected={selectedFilingInfo?.split(",")}
                  />
                </Filter.Body>
              </Filter.Portal>
            </Filter.Root>
          </div>
        </div>
        <PaginationHandler
          page_num={paginationData.currentPage}
          per_page={paginationData.itemsPerPage}
          total_count={paginationData.totalItemCount}
          total_pages={paginationData.totalPage}
          isLoading={isLoading}
          showLoadingText
        />
      </div>
    </div>
  );
};
